import React, { useState, useEffect } from "react";
import {
  X,
  EyeOff,
  MapPin,
  TrendingUp,
  XCircle,
  CheckCircle,
  Building,
  Hammer,
} from "lucide-react";
import axios from "axios";
import { MyDomain } from "../../../App";
import { useTranslation } from "react-i18next";

export default function BossPlacesNotActive({ visible, onClose, onRefresh }) {
  const { t } = useTranslation();
  const [allData, setAllData] = useState(null);
  const [activatingId, setActivatingId] = useState(null);

  // Load from localStorage whenever modal opens
  useEffect(() => {
    if (visible) {
      const data = localStorage.getItem("allData");
      if (data) setAllData(JSON.parse(data));
    }
  }, [visible]);

  if (!visible || !allData) return null;

  const inactivePlaces = (allData.myPlaces || []).filter(
    (item) => item.active === 2,
  );

  // ── Activate place ────────────────────────────────────────────────────────
  const handleActivatePlace = async (place) => {
    if (!window.confirm(t("confirmActivatePlace"))) return;

    setActivatingId(place.id);
    try {
      await axios.put(`${MyDomain}Places/UpdatePlace`, {
        Id: place.id,
        Place: place.place,
        PlaceType: place.placeType,
        Profit: place.profit || 0,
        PaidWay: place.paidWay || 1,
        PlaceDailySalary: place.placeDailySalary || null,
        Note: place.note || "",
        Active: 1,
      });

      alert(t("placeActivatedSuccessfully"));

      if (onRefresh) await onRefresh();

      // Re-read updated data
      const data = localStorage.getItem("allData");
      if (data) setAllData(JSON.parse(data));
    } catch (error) {
      console.error("❌ Error activating place:", error);
      alert(t("failedToActivatePlace"));
    } finally {
      setActivatingId(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50">
      <div
        className="bg-white rounded-t-3xl sm:rounded-3xl w-full max-w-2xl max-h-[90vh] flex flex-col animate-slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Header ── */}
        <div className="flex items-center justify-between px-5 py-5 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <EyeOff className="w-6 h-6 text-red-500" />
            <h2 className="text-xl font-bold text-gray-900">{t("inactivePlaces")}</h2>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* ── Count bar ── */}
        <div className="px-5 py-3 bg-red-50 border-b border-gray-100">
          <p className="text-sm font-semibold text-red-500">
            {inactivePlaces.length}{" "}
            {inactivePlaces.length === 1 ? "place" : "places"}
          </p>
        </div>

        {/* ── List ── */}
        <div className="flex-1 overflow-y-auto px-4 py-4">
          {inactivePlaces.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20">
              <CheckCircle className="w-16 h-16 text-green-400 mb-4" />
              <p className="text-base text-gray-500 mb-2">{t("noInactivePlaces")}</p>
              <p className="text-xs text-green-500">{t("allPlacesActive")}</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              {inactivePlaces.map((item) => {
                const isActivating = activatingId === item.id;

                return (
                  <div
                    key={item.id}
                    className="bg-white rounded-2xl p-3 border border-red-100 shadow-sm space-y-2"
                  >
                    {/* Card header */}
                    <div className="flex items-center gap-2">
                      <div className="w-9 h-9 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-gray-900 truncate">
                          {item.place}
                        </p>
                        <p className="text-[10px] text-gray-400 font-medium">
                          {t("id")}: {item.id}
                        </p>
                      </div>
                    </div>

                    {/* Type badge */}
                    <div className="flex items-center gap-1.5 bg-gray-100 px-2 py-1 rounded-lg w-fit">
                      {item.placeType === 2 ? (
                        <Hammer className="w-3 h-3 text-yellow-500" />
                      ) : (
                        <Building className="w-3 h-3 text-blue-500" />
                      )}
                      <span
                        className={`text-[10px] font-semibold ${
                          item.placeType === 2
                            ? "text-yellow-500"
                            : "text-blue-500"
                        }`}
                      >
                        {item.placeType === 2 ? "Temporary" : "Permanent"}
                      </span>
                    </div>

                    <div className="h-px bg-gray-100" />

                    {/* Profit row */}
                    <div className="flex items-center gap-1.5">
                      <TrendingUp className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                      <span className="text-[10px] text-gray-400 flex-1">
                        Profit
                      </span>
                      <span className="text-xs font-bold text-green-500">
                        ₪{(item.profit || 0).toLocaleString()}
                      </span>
                    </div>

                    {/* Inactive badge */}
                    <div className="flex items-center gap-1.5 bg-red-50 px-2 py-1 rounded-lg w-fit">
                      <XCircle className="w-3 h-3 text-red-500" />
                      <span className="text-[10px] font-semibold text-red-500">
                        {t("inactive")}
                      </span>
                    </div>

                    {/* Activate button */}
                    <button
                      onClick={() => handleActivatePlace(item)}
                      disabled={isActivating}
                      className={`w-full flex items-center justify-center gap-1.5 py-2 rounded-lg border border-green-500 text-green-600 text-xs font-semibold transition mt-1 ${
                        isActivating
                          ? "opacity-50 cursor-not-allowed bg-green-50"
                          : "bg-green-50 hover:bg-green-100"
                      }`}
                    >
                      {isActivating ? (
                        <>
                          <div className="w-3.5 h-3.5 border-2 border-green-500 border-t-transparent rounded-full animate-spin" />
                          {t("activating")}
                        </>
                      ) : (
                        <>
                          <CheckCircle className="w-4 h-4" />
                          {t("active")}
                        </>
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes slide-up {
          from { transform: translateY(100%); }
          to   { transform: translateY(0); }
        }
        .animate-slide-up { animation: slide-up 0.3s ease-out; }
        @keyframes spin { to { transform: rotate(360deg); } }
        .animate-spin { animation: spin 1s linear infinite; }
      `}</style>
    </div>
  );
}
